import React, { useState, useEffect } from 'react';
import { Box, Button, Heading, Text, VStack, useToast, Table, Thead, Tbody, Tr, Th, Td, Input, FormControl, FormLabel, Flex, Grid, Badge, Icon, Switch, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, useDisclosure, useColorModeValue } from '@chakra-ui/react';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaDownload, FaUpload, FaMedal, FaTrophy, FaTrash, FaEye, FaUserPlus } from 'react-icons/fa';
import DepartmentSelect from '../shared/DepartmentSelect';

const UploadLnaQuiz = () => {
  const [file, setFile] = useState(null);
  const [quizTitle, setQuizTitle] = useState('');
  const [assignUsers, setAssignUsers] = useState('');
  const [assignDepartments, setAssignDepartments] = useState([]);
  const [parsedData, setParsedData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);
  const [adminQuizzes, setAdminQuizzes] = useState([]);
  const [isLoadingQuizzes, setIsLoadingQuizzes] = useState(true);
  
  const [assignModalQuiz, setAssignModalQuiz] = useState(null);
  const [assignUsersInput, setAssignUsersInput] = useState('');
  const [assignDeptsInput, setAssignDeptsInput] = useState([]);
  const [isSavingAssign, setIsSavingAssign] = useState(false);
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();

  const toast = useToast();
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem('user'))?.token || "";

  const cardBg = useColorModeValue("white", "var(--bg-surface, #161428)");
  const cardBorder = useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)");
  const textDark = useColorModeValue("#0a2628", "var(--text-main, #EDE9FF)");
  const textMuted = useColorModeValue("#7a9c9f", "var(--text-muted, #9B98BC)");

  useEffect(() => {
    fetchLeaderboard();
    fetchAdminQuizzes();
  }, []);

  const fetchAdminQuizzes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminQuizzes(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingQuizzes(false);
    }
  };

  const openAssignModal = (quiz) => {
    setAssignModalQuiz(quiz);
    // Pre-fill from existing data
    const existingEmails = (quiz.assignedToUsers || []).map(u => u.email || '').filter(Boolean).join(', ');
    setAssignUsersInput(existingEmails);
    setAssignDeptsInput(quiz.assignedToDepartments || []);
    onAssignOpen();
  };

  const handleSaveAssignment = async () => {
    if (!assignModalQuiz) return;
    setIsSavingAssign(true);
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/standalone-quiz/${assignModalQuiz._id}/assign`,
        { assignUsers: assignUsersInput, assignDepartments: assignDeptsInput.join(',') },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Assignment updated successfully', status: 'success', duration: 2000 });
      setAdminQuizzes(prev =>
        prev.map(q => q._id === assignModalQuiz._id ? { ...q, ...res.data.quiz } : q)
      );
      onAssignClose();
    } catch (err) {
      toast({ title: err.response?.data?.message || 'Failed to update assignment', status: 'error', duration: 3000 });
    } finally {
      setIsSavingAssign(false);
    }
  };

  const handleToggleRetake = async (id) => {
    try {
      const res = await axios.patch(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/${id}/retake`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: res.data.message, status: 'success', duration: 2000 });
      setAdminQuizzes(prev => prev.map(q => q._id === id ? { ...q, allowRetake: res.data.allowRetake } : q));
    } catch (err) {
      toast({ title: 'Failed to update retake setting', status: 'error', duration: 3000 });
    }
  };

  const handleDeleteQuiz = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quiz?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Quiz deleted successfully', status: 'success', duration: 3000 });
      fetchAdminQuizzes();
    } catch (err) {
      toast({ title: 'Failed to delete quiz', status: 'error', duration: 3000 });
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  const handleDownloadTemplate = () => {
    const ws_data = [
      ['Department', 'Topic', 'Question', 'Option A', 'Option B', 'Option C', 'Option D', 'Correct Answer', 'Explanation'],
      ['IT', 'React', 'What is React?', 'A library', 'A framework', 'A database', 'A language', 'Option A', 'React is a library for building user interfaces.']
    ];
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LNA Template");
    XLSX.writeFile(wb, "LNA_Quiz_Template.xlsx");
  };

  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setQuizTitle(selectedFile.name.replace(/\.[^/.]+$/, "")); // Default title to filename
      parseExcel(selectedFile);
    }
  };

  const parseExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const json = XLSX.utils.sheet_to_json(worksheet);
      setParsedData(json);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast({ title: 'No valid data found in Excel', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    if (!quizTitle.trim()) {
      toast({ title: 'Please enter a quiz title', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    const department = parsedData[0].Department || parsedData[0].department || 'General';

    setIsUploading(true);
    try {
      const payload = {
        title: quizTitle,
        department,
        questions: parsedData,
        assignUsers,
        assignDepartments: assignDepartments.join(',')
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/upload`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: 'Quiz uploaded successfully!', status: 'success', duration: 3000, isClosable: true });
      setParsedData(null);
      setFile(null);
      setQuizTitle('');
      setAssignUsers('');
      setAssignDepartments([]);
      fetchAdminQuizzes();
    } catch (err) {
      console.error(err);
      toast({ 
        title: 'Upload failed', 
        description: err.response?.data?.message || err.message, 
        status: 'error', 
        duration: 4000, 
        isClosable: true 
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box p={{ base: 4, md: 8 }} w="full">
      <Flex justify="space-between" align="center" mb={8} wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg" color={textDark} fontWeight="900" letterSpacing="-1px">
            Admin LNA Quizzes
          </Heading>
          <Text color={textMuted} mt={1} fontWeight="600">
            Manage standalone quizzes and view company-wide Leaderboard metrics.
          </Text>
        </Box>
        <Button 
          leftIcon={<FaDownload />} 
          colorScheme="blue" 
          variant="outline" 
          borderRadius="full"
          onClick={handleDownloadTemplate}
        >
          Download Excel Template
        </Button>
      </Flex>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
        
        {/* Upload Column */}
        <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" p={6} boxShadow="sm">
          <Flex align="center" gap={3} mb={6}>
            <Box p={3} bg="purple.50" borderRadius="xl" color="purple.500">
              <FaUpload size={20} />
            </Box>
            <Heading size="md" color={textDark}>Upload New Quiz</Heading>
          </Flex>

          <VStack spacing={5} align="flex-start">
            <FormControl>
              <FormLabel fontWeight="bold" fontSize="sm" color={textDark}>1. Select Excel File (.xlsx)</FormLabel>
              <Input 
                type="file" 
                accept=".xlsx, .xls" 
                onChange={handleFileUpload} 
                p={1.5} 
                border="1px dashed" 
                borderColor="purple.300"
                _hover={{ borderColor: "purple.500" }}
                borderRadius="md"
              />
            </FormControl>

            {parsedData && (
              <>
                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm" color={textDark}>2. Confirm Quiz Title</FormLabel>
                  <Input 
                    value={quizTitle} 
                    onChange={(e) => setQuizTitle(e.target.value)} 
                    placeholder="e.g. Q3 Compliance Quiz" 
                    borderRadius="md"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm" color={textDark}>3. Assign to Users (Optional)</FormLabel>
                  <Input 
                    value={assignUsers} 
                    onChange={(e) => setAssignUsers(e.target.value)} 
                    placeholder="e.g. john@company.com, jane@company.com" 
                    borderRadius="md"
                  />
                  <Text fontSize="xs" color={textMuted} mt={1}>Leave blank to make public. Comma separated emails.</Text>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm" color={textDark}>4. Assign to Departments (Optional)</FormLabel>
                  <DepartmentSelect
                    isMulti
                    value={assignDepartments}
                    onChange={setAssignDepartments}
                    mt={1}
                  />
                  <Text fontSize="xs" color={textMuted} mt={2}>Leave unchecked to make public.</Text>
                </FormControl>
              </>
            )}

            {parsedData && (
              <Button 
                w="full" 
                colorScheme="purple" 
                onClick={handleSubmit} 
                isLoading={isUploading}
                borderRadius="md"
              >
                Upload {parsedData.length} Questions
              </Button>
            )}
          </VStack>

          {parsedData && parsedData.length > 0 && (
            <Box mt={6}>
              <Text fontSize="xs" fontWeight="bold" color={textMuted} mb={2} textTransform="uppercase">Preview</Text>
              <Box overflowX="auto" border="1px solid" borderColor={cardBorder} borderRadius="md">
                <Table variant="simple" size="sm">
                  <Thead bg="blackAlpha.50">
                    <Tr>
                      <Th>Dept</Th>
                      <Th>Topic</Th>
                      <Th>Question</Th>
                      <Th>Correct</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parsedData.slice(0, 3).map((row, i) => (
                      <Tr key={i}>
                        <Td fontSize="xs">{row.Department || row.department}</Td>
                        <Td fontSize="xs">{row.Topic || row.topic}</Td>
                        <Td fontSize="xs">{(row.Question || row.questionText)?.substring(0, 30)}...</Td>
                        <Td fontSize="xs" color="green.500" fontWeight="bold">{row['Correct Answer'] || row.correctAnswer}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              {parsedData.length > 3 && <Text mt={2} fontSize="xs" color={textMuted} textAlign="center">Showing first 3 of {parsedData.length} rows</Text>}
            </Box>
          )}
        </Box>

        {/* Leaderboard Column */}
        <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" p={6} boxShadow="sm">
          <Flex align="center" gap={3} mb={6}>
            <Box p={3} bg="yellow.50" borderRadius="xl" color="yellow.500">
              <FaTrophy size={20} />
            </Box>
            <Heading size="md" color={textDark}>Global Leaderboard</Heading>
          </Flex>

          {isLoadingLeaderboard ? (
            <Text>Loading...</Text>
          ) : leaderboard.length === 0 ? (
            <Text color={textMuted}>No scores available yet.</Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Rank</Th>
                    <Th>Employee</Th>
                    <Th>Dept</Th>
                    <Th isNumeric>Score</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leaderboard.map((user, idx) => (
                    <Tr key={user.userId}>
                      <Td>
                        {idx === 0 && <Icon as={FaMedal} color="yellow.400" mr={2} />}
                        {idx === 1 && <Icon as={FaMedal} color="gray.400" mr={2} />}
                        {idx === 2 && <Icon as={FaMedal} color="orange.400" mr={2} />}
                        {idx + 1}
                      </Td>
                      <Td fontWeight="bold" color={textDark}>{user.name}</Td>
                      <Td fontSize="xs">{user.department}</Td>
                      <Td isNumeric fontWeight="bold" color="purple.500">{user.totalScore}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>

      </Grid>

      {/* Admin Quizzes List */}
      <Box mt={8} bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" p={6} boxShadow="sm">
        <Heading size="md" color={textDark} mb={6}>Uploaded Quizzes</Heading>
        {isLoadingQuizzes ? (
          <Text color={textMuted}>Loading quizzes...</Text>
        ) : adminQuizzes.length === 0 ? (
          <Text color={textMuted}>No quizzes uploaded yet.</Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Title</Th>
                  <Th>Department</Th>
                  <Th>Assigned To</Th>
                  <Th>Created At</Th>
                  <Th>Allow Retake</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {adminQuizzes.map((quiz) => (
                  <Tr key={quiz._id}>
                    <Td fontWeight="bold" color={textDark}>{quiz.title}</Td>
                    <Td fontSize="xs">{quiz.department}</Td>
                    <Td fontSize="xs" maxW="160px">
                      {quiz.assignedToUsers?.length > 0 || quiz.assignedToDepartments?.length > 0 ? (
                        <Flex gap={1} wrap="wrap">
                          {quiz.assignedToDepartments?.map(d => (
                            <Badge key={d} colorScheme="blue" borderRadius="full" fontSize="9px">{d}</Badge>
                          ))}
                          {quiz.assignedToUsers?.map(u => (
                            <Badge key={u._id || u} colorScheme="purple" borderRadius="full" fontSize="9px">
                              {u.fullName || u.email || 'User'}
                            </Badge>
                          ))}
                        </Flex>
                      ) : (
                        <Badge colorScheme="gray" borderRadius="full" fontSize="9px">Public</Badge>
                      )}
                    </Td>
                    <Td fontSize="xs">{new Date(quiz.createdAt).toLocaleDateString()}</Td>
                    <Td>
                      <Switch
                        colorScheme="purple"
                        isChecked={!!quiz.allowRetake}
                        onChange={() => handleToggleRetake(quiz._id)}
                      />
                    </Td>
                    <Td>
                      <Flex gap={2} wrap="wrap">
                        <Button size="xs" colorScheme="blue" leftIcon={<FaEye />} onClick={() => navigate(`/lna-quiz/${quiz._id}`)}>
                          View
                        </Button>
                        <Button size="xs" colorScheme="teal" leftIcon={<FaUserPlus />} onClick={() => openAssignModal(quiz)}>
                          Assign
                        </Button>
                        <Button size="xs" colorScheme="red" leftIcon={<FaTrash />} onClick={() => handleDeleteQuiz(quiz._id)}>
                          Delete
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Assign Modal */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4} bg={cardBg}>
          <ModalHeader color={textDark}>
            Assign Quiz
            <Text fontSize="sm" fontWeight="600" color={textMuted} mt={1}>{assignModalQuiz?.title}</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              <FormControl>
                <FormLabel fontWeight="700" fontSize="sm" color={textDark}>Assigned Users</FormLabel>
                <Input
                  value={assignUsersInput}
                  onChange={e => setAssignUsersInput(e.target.value)}
                  placeholder="e.g. john@company.com, jane@company.com"
                  borderRadius="lg"
                />
                <Text fontSize="xs" color={textMuted} mt={1}>Comma-separated email addresses. Leave blank to remove user-specific assignment.</Text>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="700" fontSize="sm" color={textDark}>Assigned Departments</FormLabel>
                <DepartmentSelect
                  isMulti
                  value={assignDeptsInput}
                  onChange={setAssignDeptsInput}
                  mt={1}
                />
                <Text fontSize="xs" color={textMuted} mt={2}>Leave unchecked to remove department assignment (quiz becomes public).</Text>
              </FormControl>
              <Box p={3} bg="blue.50" borderRadius="lg">
                <Text fontSize="xs" color="blue.700" fontWeight="600">
                  If both fields are empty, the quiz will be visible to all employees (public).
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onAssignClose} borderRadius="full">Cancel</Button>
            <Button
              colorScheme="teal" borderRadius="full"
              leftIcon={<FaUserPlus />}
              isLoading={isSavingAssign}
              onClick={handleSaveAssignment}
            >
              Save Assignment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UploadLnaQuiz;
